import { Achado, Plano, Segmento, Serie, type Ponto } from '../../Grafico';
import { numeroBr } from '../../cptu/visual';
import { MAXIMO_DA_CORRELACAO, textoEmCm, type CorrelacaoDaEdicao } from './EdicaoDaVertical';

/**
 * O gráfico da correlação cruzada qc × fs — onda 313, Tarefa 313.6 (spec 07 §5.4: «o gráfico da correlação cruzada
 * qc × fs sugerindo a defasagem», a folha 1 do CPeT-IT).
 *
 * Tela interna (não vai ao Portal): usa o `Plano` da casa (`Grafico.tsx`), como os gráficos do laboratório. No eixo
 * x, o deslocamento do fs em cm — −(a defasagem em leituras) × o passo: o fs atrasado sobe —; no y, o r de Pearson
 * entre o qc e o fs deslocado (`correlacaoCruzada` do motor, de −50 a +50 leituras). A linha cheia são as defasagens
 * com pares suficientes; a pontilhada, as de poucos pares (ficam fora da sugestão); o círculo é a sugestão (o maior
 * r); a vertical tracejada, o deslocamento do fs que vale. A cor nunca vai sozinha: cada traço é dito embaixo.
 */
export function CorrelacaoCruzadaGrafico({ correlacao, atualCm }: {
  correlacao: CorrelacaoDaEdicao;
  /** O deslocamento do fs que vale (o do rascunho, ou o gravado), em cm. */
  atualCm: number;
}) {
  const { cc, passoCm, sugestaoCm } = correlacao;
  const pontos = cc.curva
    .filter((q) => Number.isFinite(q.r))
    .map((q) => ({ x: -q.defasagem * passoCm + 0, y: q.r, elegivel: q.elegivel }))
    .sort((a, b) => a.x - b.x);
  const cabeca = (
    <figcaption className="cptu-correlacao-titulo">
      <b>Correlação cruzada qc × fs</b> — o r entre o qc e o fs deslocado, de −{MAXIMO_DA_CORRELACAO} a +{MAXIMO_DA_CORRELACAO}{' '}
      leituras ({Number.isFinite(passoCm) ? `${numeroBr(passoCm, passoCm % 1 ? 2 : 0)} cm cada` : 'sem passo'}), sobre as
      leituras com os cortes e os picos do rascunho, sem deslocamento nem filtro.
    </figcaption>
  );
  if (pontos.length < 2 || !Number.isFinite(passoCm)) {
    return (
      <figure className="cptu-correlacao" id="cptu-correlacao-cruzada">
        {cabeca}
        <p className="cptu-nota">Sem curva: faltam leituras com qc e fs para a correlação.</p>
      </figure>
    );
  }
  const x0 = pontos[0].x;
  const x1 = pontos[pontos.length - 1].x;
  const menor = Math.min(...pontos.map((q) => q.y));
  const y0 = Math.max(-1, Math.floor(menor * 10) / 10 - 0.05);
  const janela = { x0, x1: x1 > x0 ? x1 : x0 + 1, y0: y0 < 1 ? y0 : 0.9, y1: 1 };
  const cheios: Ponto[] = pontos.filter((q) => q.elegivel).map(({ x, y }) => ({ x, y }));
  const poucos = pontos.filter((q) => !q.elegivel);
  const comAtual = Number.isFinite(atualCm) && atualCm >= janela.x0 && atualCm <= janela.x1;
  const comSugestao = Number.isFinite(sugestaoCm) && Number.isFinite(cc.r);
  return (
    <figure className="cptu-correlacao" id="cptu-correlacao-cruzada" data-sugestao-cm={comSugestao ? sugestaoCm : undefined}>
      {cabeca}
      <Plano janela={janela} tituloX="deslocamento do fs (cm) — negativo: o fs sobe" tituloY="r (qc × fs)">
        {({ px }) => (
          <>
            {poucos.length > 0 && (
              <path className="cptu-correlacao-poucos"
                d={poucos.map((q, i) => { const p = px(q); return `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`; }).join('')} />
            )}
            <Serie pontos={cheios} px={px} marcar={false} />
            {comAtual && (
              <Segmento a={{ x: atualCm, y: janela.y0 }} b={{ x: atualCm, y: janela.y1 }} px={px} classe="reta auxiliar" />
            )}
            {comSugestao && (
              <Achado p={{ x: sugestaoCm, y: cc.r }} px={px} rotulo={`${textoEmCm(sugestaoCm)} · r ${numeroBr(cc.r, 2)}`} />
            )}
          </>
        )}
      </Plano>
      <ul className="cptu-legenda-correlacao">
        <li>Linha cheia: o r de cada deslocamento com pares suficientes ({numeroBr(cc.paresMinimos, 0)} ou mais).</li>
        {poucos.length > 0 && <li>Linha pontilhada: poucos pares — fica fora da sugestão.</li>}
        {comSugestao && <li>Círculo: a sugestão, o maior r ({textoEmCm(sugestaoCm)}, r {numeroBr(cc.r, 2)}).</li>}
        {comAtual && <li>Linha vertical tracejada: o deslocamento do fs que vale agora ({textoEmCm(atualCm)}).</li>}
      </ul>
    </figure>
  );
}
